import Content, { ContentNS } from "content/content";
import useNavigateWithQuery from "features/shared/hooks/navigation/useNavigateWithQuery";
import { ChangeEvent, FC } from "react";

import SelectFileButton from "../../select-file-button/SelectFileButton";
import AllowedDocument, {
  AllowedDocumentType,
} from "models/documents/allowed-document";
import { PaxCheckDocumentSidesExpectation } from "api";
import { DocumentRequirement } from "models/checklist/checklist-item/checklist-item";
import useItemLevelNavigationState, {
  ItemNavigationState,
  ItemTypeNavigationState,
} from "features/shared/hooks/navigation/useItemLevelNavigationState";
import { useAppStore } from "store/store";
import usePaxSession from "features/shared/hooks/usePaxSession";
import { ItemLevelNavigationError } from "models/navigation/item-navigation-error";
import { useSearchParams } from "react-router-dom";
import useUploadSingleSideDocument from "features/shared/hooks/useUploadSingleSideDocument";

interface UploadButtonContentProps {
  itemType: string;
  docTitle: string;
  docs: AllowedDocument[];
  additionalPaxSessionIds?: string[];
  primary?: boolean;
  onUploadProcessStarted?: () => void;
}

interface UploadButtonByItemProps {
  item: DocumentRequirement;
  forceDocumentType?: string;
  docs: AllowedDocument[];
  additionalPaxSessionIds?: string[];
  primary?: boolean;
  onUploadProcessStarted?: () => void;
}

interface UploadButtonByTypeProps {
  itemType: string;
  docs: AllowedDocument[];
  primary?: boolean;
  onUploadProcessStarted?: () => void;
}

interface UploadButtonProps {
  primary?: boolean;
  docs: AllowedDocument[];
  additionalPaxSessionIds?: string[];
  onUploadProcessStarted?: () => void;
}

const UploadButtonContent: FC<UploadButtonContentProps> = ({
  itemType,
  docTitle,
  primary,
  docs,
  additionalPaxSessionIds,
  onUploadProcessStarted,
}) => {
  const textDocs = (s: string, args: any = {}) =>
    Content.shared.t(s, { ns: ContentNS.docs, ...args });

  const navigate = useNavigateWithQuery();

  const anyAllSidesExpectation = docs.some(
    (doc) => doc.sidesExpectation === PaxCheckDocumentSidesExpectation.ALL
  );

  const caption = `${textDocs("upload", {
    docTitle: docTitle,
  })}`;

  const [onFileSelected] = useUploadSingleSideDocument(
    docs.some((doc) => doc.expectLongProcessing === true),
    additionalPaxSessionIds,
    docs.length === 1 ? docs[0].pageIndexToProcess ?? undefined : undefined
  );

  const onUploadFileSelected = (e: ChangeEvent<HTMLInputElement>) => {
    if (!(e.target.files && e.target.files.length > 0)) {
      return;
    }

    if (anyAllSidesExpectation) {
      navigate("../upload", {
        state: {
          itemType: itemType,
          processingMayTakeLong: docs.some(
            (doc) => doc.expectLongProcessing === true
          ),
          inputFile: e.target.files[0],
          additionalPaxSessionIds: additionalPaxSessionIds,
          pageIndexToProcess:
            docs.length === 1
              ? docs[0].pageIndexToProcess ?? undefined
              : undefined,
        },
      });
    }
    if (!anyAllSidesExpectation) {
      // single side expectation
      onFileSelected(e);
    }
    onUploadProcessStarted?.();
  };

  return (
    <SelectFileButton
      primary={primary}
      onChange={onUploadFileSelected}
      label={caption}
    />
  );
};

const UploadButtonByItem: FC<UploadButtonByItemProps> = ({
  item,
  forceDocumentType,
  primary,
  docs,
  additionalPaxSessionIds,
  onUploadProcessStarted,
}) => {
  const selectedDocument = forceDocumentType
    ? item.allowedDocumentsSet.allowedDocuments.find(
        (doc) => doc.type === forceDocumentType
      )
    : undefined;

  return (
    <UploadButtonContent
      itemType={item.itemType}
      docTitle={selectedDocument?.title ?? (item.shortTitle || item.title)}
      primary={primary}
      docs={docs}
      additionalPaxSessionIds={additionalPaxSessionIds}
      onUploadProcessStarted={onUploadProcessStarted}
    />
  );
};

const UploadButtonByType: FC<UploadButtonByTypeProps> = ({
  itemType,
  primary,
  docs,
  onUploadProcessStarted,
}) => {
  const textDocs = (s: string) => Content.shared.t(s, { ns: ContentNS.docs });

  const containsOnlyPassports = !docs.some(
    (doc) => doc.type !== AllowedDocumentType.Passport
  );

  const itemTitle = containsOnlyPassports
    ? textDocs(`itemTitle.PASSPORT`)
    : textDocs(`itemTitle.TRAVEL_DOC`);

  return (
    <UploadButtonContent
      itemType={itemType}
      docTitle={itemTitle}
      primary={primary}
      docs={docs}
      onUploadProcessStarted={onUploadProcessStarted}
    />
  );
};

const UploadButton: FC<UploadButtonProps> = ({
  primary,
  docs,
  additionalPaxSessionIds,
  onUploadProcessStarted,
}) => {
  const [searchParams] = useSearchParams();
  const itemLevelNavigationState = useItemLevelNavigationState(searchParams);

  const sessionId = useAppStore((state) => state.sessionId);

  const paxSession = usePaxSession();

  if (itemLevelNavigationState instanceof ItemNavigationState) {
    // itemId
    const item = itemLevelNavigationState.item;
    if (item instanceof DocumentRequirement === false) {
      throw new ItemLevelNavigationError(
        sessionId,
        paxSession?.paxSessionId,
        `Item is not DocumentRequirement: ${item.id}`
      );
    }

    return (
      <UploadButtonByItem
        item={item as DocumentRequirement}
        forceDocumentType={itemLevelNavigationState.forceDocumentType}
        primary={primary}
        docs={docs}
        additionalPaxSessionIds={additionalPaxSessionIds}
        onUploadProcessStarted={onUploadProcessStarted}
      />
    );
  }

  if (itemLevelNavigationState instanceof ItemTypeNavigationState) {
    // itemType
    return (
      <UploadButtonByType
        itemType={itemLevelNavigationState.itemType}
        primary={primary}
        docs={docs}
        onUploadProcessStarted={onUploadProcessStarted}
      />
    );
  }

  // neither itemId or itemType - should not be the case
  throw new ItemLevelNavigationError(
    sessionId,
    paxSession?.paxSessionId,
    `neither itemId or itemType navigation state, which is odd`
  );
};

export default UploadButton;
